import React, { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import Menu from "../../../common/components/Menu/Menu";
import DesignService from "../../../service/DesignService";
import DesignStateService from "../../../service/DesignStateService";
import { Image } from "../../../types";
import { useCart } from "../../../utils/CartHooks";
import { ROUTES } from "../../../constant/common.constant";
import { useDesignFetcher } from "../../../utils/DesignsFetcherHooks";
import { useAppLoading } from "../../../context/AppLoading/useAppLoading";
import DeleteModal from "../../DeleteModal/DeleteModal";
import MoveToProjectModal from "../../MoveToProjectModal/MoveToProjectModal";
import { EditModal } from "../../EditModal";
import CreateMockupModal from "../DesignCard/components/CreateMockupModal";
import { useAuth } from "../../../common/hooks/useAuth";
import { useTeam } from "../../../context/Team";
import LockDesignModal from "../DesignCard/components/LockDesignModal";
import { useDesign } from "../../../context/Design/useDesign";

interface Props {
  designId: string;
  designName: string;
  designImages: Image[];
  projectId: string;
  projectName: string;
  canDeleteDesign: boolean;
  canRenameDesign: boolean;
  canMoveDesign?: boolean;
  canAddToCart?: boolean;
  canDuplicateDesign?: boolean;
  redirectToEditorOnDuplicate?: boolean;
}

const DesignMenu = (props: Props) => {
  const { t } = useTranslation();
  const { teamId: teamIdFromContext } = useTeam();
  const { isAdmin } = useAuth();
  const { design, onDuplicate } = useDesign();
  const teamId = teamIdFromContext || design?.project?.teamId;

  const navigate = useNavigate();

  const {
    designId,
    designName,
    designImages,
    projectId,
    projectName,
    canDeleteDesign = true,
    canRenameDesign = true,
    canMoveDesign = true,
    canAddToCart = true,
    canDuplicateDesign = true,
    redirectToEditorOnDuplicate,
  } = props;

  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMoveProjectModal, setShowMoveProjectModal] = useState(false);
  const [showCreateMockupModal, setShowCreateMockupModal] = useState(false);
  const [showLockDesignModal, setShowLockDesignModal] = useState(false);

  const { refetchDesign } = useDesign();
  const { addToCart, fetchCartItemsAndUpdateStore } = useCart();
  const { setIsAppLoading } = useAppLoading();

  const onRenameCloseClick = () => {
    setShowRenameModal(false);
  };

  const onDeleteClickClose = () => {
    setShowDeleteModal(false);
  };
  const onMoveClickClose = () => {
    setShowMoveProjectModal(false);
  };
  const onRename = useCallback(
    async (name: string) => {
      try {
        await DesignService.renameDesign({ name, designId: designId });
        refetchDesign();
      } catch (e) {
        console.error(e);
      } finally {
        setShowRenameModal(false);
      }
    },
    [designId, refetchDesign]
  );

  const onDelete = useCallback(async () => {
    try {
      setIsAppLoading(true);
      await DesignService.deleteDesign(designId);
      navigate(generatePath(ROUTES.PROJECT, { projectId, teamId }));
      setShowDeleteModal(false);
      fetchCartItemsAndUpdateStore(); //Because deleting a design can impact the cart
      setIsAppLoading(false);
    } catch (e) {
      console.error(e);
      setIsAppLoading(false);
    }
  }, [
    setIsAppLoading,
    designId,
    navigate,
    projectId,
    teamId,
    fetchCartItemsAndUpdateStore,
  ]);

  const { fetchProductDefinition } = useDesignFetcher();

  const onAddToCart = async () => {
    //First get the Design State info of the design, then add to cart the design
    const {
      data: { design },
    } = await DesignStateService.getDesignState(designId);

    const {
      productDefinition: { sizes },
    } = await fetchProductDefinition(design.family, design.product);

    addToCart(designId, sizes, design.product.name);
  };

  return (
    <React.Fragment>
      <EditModal
        open={showRenameModal}
        onSave={onRename}
        defaultValue={designName || t("design.untitled_design")}
        onClose={onRenameCloseClick}
        title={t("design.rename_title")}
        ctaButtonText={t("general.rename")}
      />
      <DeleteModal
        open={showDeleteModal}
        onDelete={onDelete}
        title={t("design.delete_modal_title")}
        message={
          design?.locked
            ? t("design.delete_modal_message_locked")
            : t("design.delete_modal_message")
        }
        hideCta={design?.locked}
        onClose={onDeleteClickClose}
      />
      <MoveToProjectModal
        open={showMoveProjectModal}
        onClose={onMoveClickClose}
        designId={designId}
        projectId={projectId}
      />
      {teamId && (
        <CreateMockupModal
          open={showCreateMockupModal}
          onClose={() => setShowCreateMockupModal(false)}
          teamId={teamId}
          designs={[
            {
              designId,
              designName,
              images: designImages,
              lockedVersion: design?.lockedVersion,
              family: design?.family,
              product: design?.product,
              productLine: design?.productLine,
              decorations: design?.design?.decorations,
            },
          ]}
          projectName={projectName}
        />
      )}
      {refetchDesign && (
        <LockDesignModal
          open={showLockDesignModal}
          onClose={() => setShowLockDesignModal(false)}
          designId={designId}
          oldVersion={design?.lockedVersion}
          onUpdate={refetchDesign}
        />
      )}

      <Menu
        options={[
          {
            labelKey: "general.rename",
            onClick: () => setShowRenameModal(true),
            disabled: !canRenameDesign,
          },
          {
            labelKey: "design.delete_menu",
            onClick: () => setShowDeleteModal(true),
            disabled: !canDeleteDesign,
          },
          {
            labelKey: "design.add_to_cart",
            onClick: onAddToCart,
            disabled: !canAddToCart,
          },
          {
            labelKey: "design.duplicate",
            onClick: () => onDuplicate(redirectToEditorOnDuplicate),
            disabled: !canDuplicateDesign,
          },
          {
            labelKey: "design.move_project",
            onClick: () => setShowMoveProjectModal(true),
            disabled: !canMoveDesign,
          },
          {
            labelKey: "modal.create_mockup.title",
            onClick: () => setShowCreateMockupModal(true),
          },
          {
            labelKey: design?.locked ? "design.unlock" : "design.lock",
            onClick: async () => {
              if (design?.locked) {
                await DesignService.lockDesign(
                  designId,
                  false,
                  design?.lockedVersion ?? ""
                );
                refetchDesign();
              } else {
                setShowLockDesignModal(true);
              }
            },
            hideIf: !isAdmin,
            adminOnly: true,
          },
        ]}
      />
    </React.Fragment>
  );
};

export default DesignMenu;
